import { getAuthorized } from "@/lib/fetch";
import type {
  AnalyticsData,
  AnalyticsViewData,
  AuditData,
  CARow,
  DocMeta,
  FORow,
  Fund,
  PortfolioRow,
} from "../model/types";

const backendBaseUrl =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8000";
const BASE_URL = `${backendBaseUrl.replace(/\/+$/, "")}/s2`;

export function buildS2DownloadUrl(path: string): string {
  return `${BASE_URL}/${path}`;
}

export function getPortfolioPositionsDownloadUrl(): string {
  return buildS2DownloadUrl("portfolio-positions/download-excel");
}

/** Returns the direct URL for a PDF — usable as fetch URL or new-tab href */
export function getPdfUrl(docId: string): string {
  return `${BASE_URL}/pdf/${encodeURIComponent(docId)}`;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await getAuthorized(`${BASE_URL}${path}`);
  return response.json();
}

export const api = {
  /** Full docMeta map (doc_id → DocMeta) */
  getDocMeta(): Promise<Record<string, DocMeta>> {
    return getJson("/docs/meta");
  },

  /** List all funds with their doc_ids and metadata */
  getFunds(): Promise<Fund[]> {
    return getJson("/funds");
  },

  /** Capital account rows. Use doc_id='Merge' for golden rows. */
  getCapitalAccount(fundId: string, docId: string): Promise<CARow[]> {
    return getJson(`/capital-account/${fundId}/${docId}`);
  },

  /** Fund overview rows. Use doc_id='Merge' for golden rows. */
  getFundOverview(fundId: string, docId: string): Promise<FORow[]> {
    return getJson(`/fund-overview/${fundId}/${docId}`);
  },

  /** Portfolio rows (gen_info JOIN positions). Use doc_id='Merge' for golden rows. */
  getPortfolio(fundId: string, docId: string): Promise<PortfolioRow[]> {
    return getJson(`/portfolio/${fundId}/${docId}`);
  },

  /** Pre-aggregated analytics (fund-level time series + portfolio positions). */
  getAnalytics(fundId: string): Promise<AnalyticsData> {
    return getJson(`/analytics/${fundId}`);
  },

  /** All capital-account + portfolio + investments for a fund in one call. */
  getAnalyticsViewData(fundId: string): Promise<AnalyticsViewData> {
    return getJson(`/analytics-data/${fundId}`);
  },

  /** Audit and merge texts for a doc_id. */
  getDocAudit(docId: string): Promise<AuditData> {
    return getJson(`/audit/${docId}`);
  },

  /** Investment registry (json0_inv) — entry/exit dates, status per investment. */
  getInvestments(fundId: string): Promise<Record<string, string>[]> {
    return getJson(`/investments/${fundId}`);
  },

  /** One row per fund from merge FO — for the Funds Overview table. */
  getAllFundsOverview(): Promise<Record<string, string>[]> {
    return getJson("/funds-overview");
  },
};
